import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { connectWindex, disconnectWindex, getWindexStatus } from '../../api';
import { ActionConfirmModal } from '../ActionConfirmModal';

const SCHOOL_CODE_IN_USE =
  'This school code is already connected to another company.';
const API_NOT_CONFIGURED = 'Windex API is not configured.';

function errorCode(error) {
  return error?.response?.data?.code || '';
}

function fieldDetail(error) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : 'Invalid school code.';
}

export function WindexSchoolCodeForm({
  companyId,
  initialCode = '',
  onDone,
  onCancel,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [schoolCode, setSchoolCode] = useState(initialCode || '');
  const [fieldError, setFieldError] = useState('');
  const [apiDown, setApiDown] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const statusKey = ['windex-status', companyId];

  const {
    data: status,
    isError,
    error,
  } = useQuery({
    queryKey: statusKey,
    queryFn: () => getWindexStatus(companyId),
    enabled: !!companyId,
    retry: false,
  });

  useEffect(() => {
    if (!isError) return;
    const statusCode = error?.response?.status;
    const code = errorCode(error);
    if (statusCode === 404 && code === 'company_not_found') {
      navigate('/integrations', { replace: true });
      return;
    }
    if (statusCode === 503 && code === 'windex_api_error') {
      setApiDown(true);
      setFieldError(API_NOT_CONFIGURED);
    }
  }, [isError, error, navigate]);

  useEffect(() => {
    if (seeded || !status) return;
    if (status.is_connected) {
      setSchoolCode(status.school_code || initialCode || '');
    } else {
      setSchoolCode('');
    }
    setSeeded(true);
  }, [status, seeded, initialCode]);

  const patchCard = (connected, code) => {
    queryClient.setQueryData(['company-integrations', companyId], (prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) =>
        item.integration_slug === 'windex'
          ? {
              ...item,
              is_connected: connected,
              config: { ...item.config, school_code: code },
            }
          : item,
      );
    });
    queryClient.invalidateQueries({
      queryKey: ['company-integrations', companyId],
    });
  };

  const handleWindexError = (err, { onNotConnected } = {}) => {
    const statusCode = err?.response?.status;
    const code = errorCode(err);
    if (statusCode === 400 && code === 'windex_api_error') {
      setFieldError(fieldDetail(err));
      return;
    }
    if (statusCode === 409 && code === 'windex_school_code_in_use') {
      setFieldError(SCHOOL_CODE_IN_USE);
      return;
    }
    if (statusCode === 404 && code === 'windex_not_connected') {
      onNotConnected?.();
      return;
    }
    if (statusCode === 404 && code === 'company_not_found') {
      navigate('/integrations', { replace: true });
      return;
    }
    if (statusCode === 503 && code === 'windex_api_error') {
      setApiDown(true);
      setFieldError(API_NOT_CONFIGURED);
    }
  };

  const connectMutation = useMutation({
    mutationFn: (code) => connectWindex(companyId, code),
    onSuccess: (body, code) => {
      const saved = body?.school_code || code;
      queryClient.setQueryData(statusKey, {
        ...body,
        school_code: saved,
        is_connected: true,
      });
      patchCard(true, saved);
      toast.success('Windex connected.');
      onDone?.();
    },
    onError: (err) => handleWindexError(err),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectWindex(companyId),
    onSuccess: (body) => {
      queryClient.setQueryData(statusKey, {
        ...body,
        school_code: '',
        is_connected: false,
        integration_status: body?.integration_status || 'disconnected',
      });
      setSchoolCode('');
      patchCard(false, '');
      queryClient.invalidateQueries({ queryKey: statusKey });
      queryClient.invalidateQueries({
        queryKey: ['company-integrations', companyId],
      });
      toast.success('Windex disconnected.');
      onDone?.();
    },
    onError: (err) =>
      handleWindexError(err, {
        onNotConnected: () => {
          queryClient.invalidateQueries({ queryKey: statusKey });
        },
      }),
  });

  const busy = connectMutation.isPending || disconnectMutation.isPending;
  const connected = !!status?.is_connected;

  const handleCancel = () => {
    setSchoolCode(status?.is_connected ? status?.school_code || '' : '');
    setFieldError('');
    onCancel?.();
  };

  const submit = (event) => {
    event.preventDefault();
    const code = schoolCode.trim();
    if (!code) {
      setFieldError('Enter a school code.');
      return;
    }
    setSchoolCode(code);
    setFieldError('');
    connectMutation.mutate(code);
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <form onSubmit={submit} className="mt-4 flex w-full flex-col gap-2">
        <label
          className="text-[13px] font-medium text-gray-700"
          htmlFor="windex_school_code"
        >
          School code
        </label>
        <input
          id="windex_school_code"
          name="school_code"
          maxLength={64}
          value={schoolCode}
          placeholder="School code"
          autoComplete="off"
          disabled={apiDown || busy}
          onChange={(event) => setSchoolCode(event.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900"
        />
        {fieldError ? (
          <p className="text-xs text-rose-600">{fieldError}</p>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={busy}
            className="border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          {connected ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmOpen(true)}
              className="border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-semibold px-3 py-2 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {disconnectMutation.isPending ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-rose-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Disconnecting...</span>
                </>
              ) : (
                'Disconnect'
              )}
            </button>
          ) : null}
          <button
            type="submit"
            disabled={busy || apiDown}
            className="bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-70 flex items-center justify-center gap-2 min-w-[96px] transition-colors"
          >
            {connectMutation.isPending ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>{connected ? 'Saving...' : 'Connecting...'}</span>
              </>
            ) : connected ? (
              'Save'
            ) : (
              'Connect'
            )}
          </button>
        </div>
        <ActionConfirmModal
          isOpen={confirmOpen}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            disconnectMutation.mutate();
          }}
          isPending={disconnectMutation.isPending}
          title="Disconnect Windex"
          description="Disconnect Windex for this company?"
          confirmLabel="Disconnect"
          pendingLabel="Disconnecting..."
          variant="danger"
        />
      </form>
    </div>
  );
}
